import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, limit } from 'firebase/firestore';
import type { Contact, ImportContactsResult } from '@revbase/shared';
import {
  Users,
  Upload,
  ShieldBan,
  ShieldCheck,
  AlertCircle,
} from 'lucide-react';
import { db } from '../lib/firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { api } from '../lib/api.js';
import {
  Button,
  Input,
  Badge,
  PageHeader,
  CardTitle,
  SectionCard,
  EmptyState,
} from '../components/ui.js';

interface ContactRow extends Contact {
  id: string;
}

/**
 * A compact per-contact enrichment indicator. Shows a PhishFence letter grade
 * when one is known, else a status pill. Never asserts a claim here; this is
 * just a freshness/coverage cue for the operator.
 */
function EnrichmentCell({ c }: { c: ContactRow }) {
  const e = c.enrichment;
  if (!e) return <span className="text-slate-400">not checked</span>;
  if (e.status === 'no_domain')
    return <Badge variant="neutral">not applicable</Badge>;
  if (e.status === 'lookup_failed')
    return <Badge variant="warn">check failed</Badge>;
  if (e.status === 'pending')
    return <Badge variant="info">checking</Badge>;
  // status === 'ok': prefer a PhishFence letter grade, else a DMARC summary.
  if (e.grade && /^[A-F]$/.test(e.grade))
    return <Badge variant="brand">grade {e.grade}</Badge>;
  if (e.hasDmarc === false)
    return <Badge variant="warn">no DMARC</Badge>;
  if (e.hasDmarc === true)
    return <Badge variant="good">DMARC</Badge>;
  return <Badge variant="neutral">checked</Badge>;
}

export function Contacts() {
  const { tenantId } = useAuth();
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportContactsResult | null>(null);
  const [suppressEmail, setSuppressEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enriching, setEnriching] = useState(false);
  const [enrichMsg, setEnrichMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    const q = query(collection(db, `tenants/${tenantId}/contacts`), limit(100));
    return onSnapshot(q, (snap) => {
      setContacts(
        snap.docs.map((d) => ({ id: d.id, ...(d.data() as Contact) })),
      );
    });
  }, [tenantId]);

  const onFile = async (file: File) => {
    setImporting(true);
    setError(null);
    setResult(null);
    try {
      const csv = await file.text();
      setResult(await api.importContacts({ csv }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const enrich = async () => {
    if (!contacts.length) return;
    setEnriching(true);
    setError(null);
    setEnrichMsg(null);
    try {
      // Enrich the loaded contacts; the server dedupes by domain so many
      // contacts at one company cost one check.
      const res = await api.enrichContacts({
        contactIds: contacts.map((c) => c.id),
      });
      setEnrichMsg(
        `Checked ${res.domains} ${res.domains === 1 ? 'domain' : 'domains'}, updated ${res.contacts} ${res.contacts === 1 ? 'contact' : 'contacts'}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enrichment failed');
    } finally {
      setEnriching(false);
    }
  };

  const suppress = async () => {
    if (!suppressEmail) return;
    setError(null);
    try {
      await api.addSuppression({ email: suppressEmail });
      setSuppressEmail('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to suppress');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader icon={Users} eyebrow="Lists" title="Contacts" />

      <SectionCard title="Import CSV" icon={Upload} bodyClassName="space-y-3">
        <p className="text-sm text-slate-600">
          Header row required. Recognized columns:{' '}
          <code className="font-mono text-slate-900">email</code> (required),
          <code className="font-mono text-slate-900"> firstName</code>,{' '}
          <code className="font-mono text-slate-900">lastName</code>,{' '}
          <code className="font-mono text-slate-900">company</code>,
          <code className="font-mono text-slate-900"> title</code>. Others become
          custom fields.
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={importing}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void onFile(f);
          }}
          className="text-sm text-slate-600 file:mr-3 file:rounded-lg file:border file:border-slate-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-50"
        />
        {importing && <p className="text-sm text-slate-600">Importing…</p>}
        {result && (
          <p className="text-sm text-slate-900">
            Imported {result.imported}, {result.duplicates} duplicates,{' '}
            {result.suppressed} suppressed, {result.invalid} invalid.
          </p>
        )}
      </SectionCard>

      <SectionCard title="Add suppression" icon={ShieldBan}>
        <div className="flex gap-2">
          <Input
            placeholder="email@example.com"
            value={suppressEmail}
            onChange={(e) => setSuppressEmail(e.target.value)}
          />
          <Button variant="ghost" onClick={() => void suppress()}>
            Suppress
          </Button>
        </div>
      </SectionCard>

      <SectionCard
        title="Enrich with PhishFence"
        icon={ShieldCheck}
        bodyClassName="space-y-3"
      >
        <p className="text-sm text-slate-600">
          Check each contact's domain for real email-security findings (DMARC,
          SPF, and a PhishFence grade). These power the{' '}
          <code className="font-mono text-slate-900">{'{{dmarcStatus}}'}</code>,{' '}
          <code className="font-mono text-slate-900">{'{{phishfenceReport}}'}</code>{' '}
          and related merge variables in your campaigns. Deduped by domain, so
          many contacts at one company cost one check.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={() => void enrich()}
            disabled={enriching || contacts.length === 0}
          >
            {enriching ? 'Enriching…' : 'Enrich loaded contacts'}
          </Button>
          {enrichMsg && (
            <span className="text-sm text-slate-600">{enrichMsg}</span>
          )}
        </div>
      </SectionCard>

      {error && (
        <div
          role="alert"
          className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <AlertCircle className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
          {error}
        </div>
      )}

      <section>
        <CardTitle icon={Users} className="mb-4 text-lg">
          Contacts{' '}
          <span className="font-normal text-slate-400">
            ({contacts.length})
          </span>
        </CardTitle>
        {contacts.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No contacts yet"
            description="Import a CSV above to build your list. Enrichment then checks each domain for real security findings."
          />
        ) : (
          <div className="card overflow-x-auto p-0">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Company</th>
                  <th>Security</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.id}>
                    <td className="font-mono text-[13px] text-slate-900">
                      {c.email}
                    </td>
                    <td>
                      {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                    </td>
                    <td className="text-slate-500">{c.company ?? ''}</td>
                    <td className="text-xs">
                      <EnrichmentCell c={c} />
                    </td>
                    <td>
                      {c.suppressed ? (
                        <Badge variant="bad" dot>
                          suppressed
                        </Badge>
                      ) : (
                        <Badge variant="neutral" dot>
                          {c.status}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
